import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { t } from '@/i18n';

interface PublicConfig {
  oauth: { github: boolean; google: boolean };
  signup: boolean;
  announcement: { text: string; tone: 'info' | 'warning' } | null;
  publicBoards: boolean;
  smtp: boolean;
}

export function usePublicConfig() {
  return useQuery({ queryKey: ['config'], queryFn: () => api<PublicConfig>('/api/config'), staleTime: 5 * 60_000 });
}

function GithubIcon() {
  return (
    <svg viewBox="0 0 24 24" className="size-4" fill="currentColor" aria-hidden>
      <path d="M12 .5a11.5 11.5 0 0 0-3.64 22.41c.58.1.79-.25.79-.56v-2c-3.2.7-3.88-1.37-3.88-1.37-.53-1.33-1.29-1.69-1.29-1.69-1.05-.72.08-.7.08-.7 1.16.08 1.77 1.19 1.77 1.19 1.03 1.77 2.71 1.26 3.37.96.1-.75.4-1.26.73-1.55-2.56-.29-5.25-1.28-5.25-5.68 0-1.26.45-2.28 1.19-3.09-.12-.29-.52-1.46.11-3.05 0 0 .97-.31 3.17 1.18a11 11 0 0 1 5.77 0c2.2-1.49 3.17-1.18 3.17-1.18.63 1.59.23 2.76.11 3.05.74.81 1.19 1.83 1.19 3.09 0 4.41-2.7 5.38-5.27 5.67.41.36.78 1.06.78 2.14v3.17c0 .31.21.67.8.56A11.5 11.5 0 0 0 12 .5Z" />
    </svg>
  );
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="size-4" aria-hidden>
      <path fill="#4285F4" d="M23.5 12.27c0-.82-.07-1.6-.2-2.36H12v4.46h6.45a5.5 5.5 0 0 1-2.39 3.61v3h3.86c2.26-2.08 3.58-5.15 3.58-8.71Z" />
      <path fill="#34A853" d="M12 24c3.24 0 5.95-1.07 7.93-2.9l-3.86-3c-1.08.72-2.45 1.15-4.07 1.15-3.13 0-5.78-2.11-6.73-4.95H1.28v3.1A12 12 0 0 0 12 24Z" />
      <path fill="#FBBC05" d="M5.27 14.3a7.2 7.2 0 0 1 0-4.6V6.6H1.28a12 12 0 0 0 0 10.8l3.99-3.1Z" />
      <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42A11.5 11.5 0 0 0 12 0 12 12 0 0 0 1.28 6.6l3.99 3.1C6.22 6.86 8.87 4.75 12 4.75Z" />
    </svg>
  );
}

/** Кнопки OAuth показываются, только если провайдеры настроены через env. */
export function OAuthButtons({ next }: { next: string | null }) {
  const { data } = usePublicConfig();
  if (!data || (!data.oauth.github && !data.oauth.google)) return null;
  const q = next ? `?next=${encodeURIComponent(next)}` : '';
  return (
    <div className="mb-6 flex flex-col gap-2">
      {data.oauth.github && (
        <a href={`/api/auth/oauth/github${q}`} className="flex h-10 items-center justify-center gap-2 rounded-control bg-surface text-sm font-medium shadow-[inset_0_0_0_1px_var(--lumo-border-strong)] hover:bg-hover">
          <GithubIcon /> {t('auth.withGithub')}
        </a>
      )}
      {data.oauth.google && (
        <a href={`/api/auth/oauth/google${q}`} className="flex h-10 items-center justify-center gap-2 rounded-control bg-surface text-sm font-medium shadow-[inset_0_0_0_1px_var(--lumo-border-strong)] hover:bg-hover">
          <GoogleIcon /> {t('auth.withGoogle')}
        </a>
      )}
      <div className="my-2 flex items-center gap-3 text-xs text-fg-subtle">
        <span className="h-px flex-1 bg-border" />
        {t('auth.orContinue')}
        <span className="h-px flex-1 bg-border" />
      </div>
    </div>
  );
}
